import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PurchaseStatus } from '@construction/shared';
import { FilterBar } from '@/components/ui';
import { RangeField } from '@/features/filters/RangeField';
import { StatusTabs } from '@/features/filters/StatusTabs';

/** 页签上的数字：只有待批准 / 待报销带（不限日期，CR-049）；拿不到就不写 */
export type PurchaseCounts = Partial<Record<PurchaseStatus, number>>;

/** 待批准 / 待报销页签下那句说明。显式映射，不靠拼 key */
const OPEN_HINT: Partial<Record<PurchaseStatus, string>> = {
  [PurchaseStatus.PENDING]: 'purchases.openHintPENDING',
  [PurchaseStatus.APPROVED]: 'purchases.openHintAPPROVED',
};

/** 列表为空时的标题：待处理的页签不提「这段时间」—— 它们本来就不看日期 */
export function purchaseEmptyKey(status: PurchaseStatus | null): string {
  if (status === PurchaseStatus.PENDING) return 'purchases.emptyPENDING';
  if (status === PurchaseStatus.APPROVED) return 'purchases.emptyAPPROVED';
  return 'purchases.empty';
}

/**
 * 采购的状态页签 + 工具条（CR-050，CR-082 重做）。
 *
 * **状态是页签，日期只在查记录的页签里**（CR-050，客户原话「又是日期，又是全部，会迷糊了」）。
 * 照 Xero / QuickBooks / Zoho Expense / Ramp 的账单页：
 * - 待批准 N ｜ 待报销 N：等人处理的，不按日期（CR-049），工具条上是一句灰字「最早的在前」；
 * - 已报销 ｜ 已驳回 ｜ 全部记录：查历史的，工具条上是日期胶囊。
 * 被驳回的必须有自己的页签 —— 那是垫了钱却要不回来的几笔。
 *
 * 工具条：左边 `leading`（本工地 / 我的）+ 日期胶囊或那句灰字，右边 `summary`（几笔、合计多少，服务端聚合）。
 * **「本工地」和「我的」两个 tab 共用**（CR-048）：页签和区间放在页面上，切 tab 不变。
 * 采购按月看（报销周期是月度的），所以没有「今日 / 昨日」。下载在页头（CR-082）。
 */
export function PurchaseFilters({
  range,
  onRangeChange,
  today,
  status,
  onStatusChange,
  counts,
  leading,
  summary,
}: {
  range: { from: string; to: string };
  onRangeChange: (range: { from: string; to: string }) => void;
  /** 工地当地的今天 */
  today: string;
  status: PurchaseStatus | null;
  onStatusChange: (status: PurchaseStatus | null) => void;
  counts?: PurchaseCounts;
  /** 工具条最左边：本工地 / 我的 */
  leading?: ReactNode;
  /** 工具条右边的汇总 */
  summary?: ReactNode;
}) {
  const { t } = useTranslation();
  const hint = status ? OPEN_HINT[status] : undefined;

  return (
    <>
      <StatusTabs<PurchaseStatus>
        tabs={[
          { value: PurchaseStatus.PENDING, label: t('purchases.statusPENDING'), count: counts?.PENDING, attention: true },
          { value: PurchaseStatus.APPROVED, label: t('purchases.statusAPPROVED'), count: counts?.APPROVED, attention: true },
          { value: PurchaseStatus.REIMBURSED, label: t('purchases.statusREIMBURSED') },
          { value: PurchaseStatus.REJECTED, label: t('purchases.statusREJECTED') },
          { value: null, label: t('purchases.tabAll') },
        ]}
        value={status}
        onChange={onStatusChange}
      />

      <div className="mt-3">
        <FilterBar summary={summary}>
          {leading}
          {hint ? (
            <span className="text-caption text-ink-3">{t(hint)}</span>
          ) : (
            <RangeField
              from={range.from}
              to={range.to}
              today={today}
              presets={['thisWeek', 'lastWeek', 'lastTwoWeeks', 'thisMonth']}
              onChange={onRangeChange}
            />
          )}
        </FilterBar>
      </div>
    </>
  );
}
